import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateFaq } from "@/features/faq/hooks/use-faq";
import type { Faq } from "@/features/faq/types";
import { useTranslation } from "@/features/utils/i18n";
import { XIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const CATEGORY_LIST_ID = "faq-category-suggestions";

export const RefineFaqDialog = ({
  faq,
  categories = [],
  open,
  onOpenChange,
}: {
  faq: Faq;
  categories?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation("faq");
  const updateFaq = useUpdateFaq();

  const [question, setQuestion] = useState(faq.question);
  const [category, setCategory] = useState(faq.category);
  const [variations, setVariations] = useState<string[]>(faq.variations || []);
  const [newVariation, setNewVariation] = useState("");
  const [refinedResponse, setRefinedResponse] = useState(
    faq.refined_response || faq.ai_response || "",
  );

  // Reset local state whenever a different FAQ is opened.
  useEffect(() => {
    if (open) {
      setQuestion(faq.question);
      setCategory(faq.category);
      setVariations(faq.variations || []);
      setNewVariation("");
      setRefinedResponse(faq.refined_response || faq.ai_response || "");
    }
  }, [open, faq]);

  const addVariation = () => {
    const value = newVariation.trim();
    if (!value || variations.includes(value)) {
      setNewVariation("");
      return;
    }
    setVariations([...variations, value]);
    setNewVariation("");
  };

  const removeVariation = (index: number) => {
    setVariations(variations.filter((_, i) => i !== index));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateFaq.mutateAsync({
        id: faq.id,
        data: {
          question,
          category,
          variations,
          refined_response: refinedResponse,
        },
      });
      toast.success(t("messages.saveSuccess"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.saveError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSave}>
          <DialogHeader>
            <DialogTitle>{t("refine.title")}</DialogTitle>
            <DialogDescription>{t("refine.description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="faq-question">{t("refine.question")}</Label>
              <Input
                id="faq-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="faq-category">{t("refine.category")}</Label>
              <Input
                id="faq-category"
                list={CATEGORY_LIST_ID}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder={t("refine.categoryPlaceholder")}
              />
              <datalist id={CATEGORY_LIST_ID}>
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label>{t("refine.variations")}</Label>
              <p className="text-muted-foreground text-xs">
                {t("refine.variationsHint")}
              </p>
              {variations.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {variations.map((v, i) => (
                    <span
                      key={`${v}-${i}`}
                      className="bg-muted text-muted-foreground flex items-center gap-1 rounded-md px-2 py-1 text-xs"
                    >
                      {v}
                      <button
                        type="button"
                        onClick={() => removeVariation(i)}
                        className="hover:text-foreground"
                        aria-label={t("refine.removeVariation")}
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newVariation}
                  onChange={(e) => setNewVariation(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addVariation();
                    }
                  }}
                  placeholder={t("refine.addVariationPlaceholder")}
                />
                <Button type="button" variant="outline" onClick={addVariation}>
                  {t("refine.addVariation")}
                </Button>
              </div>
            </div>

            {faq.ai_response && (
              <div className="space-y-2">
                <Label>{t("refine.aiResponse")}</Label>
                <div className="bg-muted/50 text-muted-foreground rounded-md border p-3 text-sm whitespace-pre-wrap">
                  {faq.ai_response}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="faq-refined">{t("refine.refinedResponse")}</Label>
              <Textarea
                id="faq-refined"
                value={refinedResponse}
                onChange={(e) => setRefinedResponse(e.target.value)}
                rows={6}
                placeholder={t("refine.refinedPlaceholder")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("refine.cancel")}
            </Button>
            <Button type="submit" disabled={updateFaq.isPending}>
              {t("refine.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
